import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/appLogoFinalLight.svg';

const navLinks = [
  { name: 'Features', href: '/feature-highlight', internal: true },
  { name: 'Download', href: '/download', internal: true },
];

const companyLinks = [
  { name: 'About Ames', href: 'https://www.amesengineering.com/about' },
  { name: 'Contact Sales', href: 'https://www.amesengineering.com/sales/salesRequest' },
  { name: 'Support', href: 'https://www.amesengineering.com/support/supportRequest' },
  { name: 'Our Products', href: 'https://www.amesengineering.com/all-products' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const { user } = useAuth();

  const openMenu = () => {
    setMobileMenuOpen(true);
    requestAnimationFrame(() => setMenuVisible(true));
  };

  const closeMenu = () => {
    setMenuVisible(false);
    setTimeout(() => {
      setMobileMenuOpen(false);
      setCompanyOpen(false);
    }, 300);
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-200">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8 md:py-4">
          <div className="flex lg:flex-1">
            <Link to="/" className="-m-1.5 p-1.5">
              <span className="sr-only">Ames Connect</span>
              <img src={logo} alt="Ames Connect Logo" className="h-8 w-auto" />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={openMenu}
              className="text-gray-700 p-2"
            >
              <span className="sr-only">Open menu</span>
              <Bars3Icon className="w-6 h-6" />
            </button>
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex lg:gap-x-8 items-center">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
              >
                {link.name}
              </Link>
            ))}

            {/* Company dropdown */}
            <div className="relative">
              <button
                onClick={() => setCompanyOpen(!companyOpen)}
                className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-1"
              >
                Company
                <svg
                  className={`w-4 h-4 transition-transform ${companyOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {companyOpen && (
                <>
                  <div className="fixed inset-0" onClick={() => setCompanyOpen(false)} />
                  <div className="absolute right-0 top-full mt-3 w-48 bg-white border border-gray-200 rounded-sm shadow-lg py-2 z-10">
                    {companyLinks.map((link) => (
                      <a
                        key={link.name}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                        onClick={() => setCompanyOpen(false)}
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-4 items-center">
            {user ? (
              <Link
                to="/home"
                className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
              >
                Dashboard <span aria-hidden="true">&rarr;</span>
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  className="text-sm font-medium bg-red-600 text-white px-4 py-2 rounded-sm hover:bg-red-700 transition-colors"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        </nav>
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div
          className={`fixed inset-0 z-[100] lg:hidden flex flex-col px-6 py-6 bg-white transition-transform duration-300 ease-out ${
            menuVisible ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between">
            <Link to="/" className="-m-1.5 p-1.5" onClick={closeMenu}>
              <img src={logo} alt="Ames Connect Logo" className="h-8 w-auto" />
            </Link>
            <button onClick={closeMenu} className="text-gray-700 p-2">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="mt-8 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="block py-4 text-xl font-medium text-gray-700 hover:text-gray-900 border-b border-gray-100 transition-colors"
                onClick={closeMenu}
              >
                {link.name}
              </Link>
            ))}

            {/* Company section */}
            <div className="py-3 border-b border-gray-100">
              <button
                onClick={() => setCompanyOpen(!companyOpen)}
                className="flex items-center justify-between w-full text-xl font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                Company
                <svg
                  className={`w-4 h-4 transition-transform ${companyOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {companyOpen && (
                <div className="mt-2 ml-4 space-y-1">
                  {companyLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-2 text-base text-gray-500 hover:text-gray-900 transition-colors"
                      onClick={closeMenu}
                    >
                      {link.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Auth links */}
            <div className="pt-4 space-y-3">
              {user ? (
                <Link
                  to="/home"
                  className="block py-4 text-xl font-medium text-red-600 hover:text-red-700 transition-colors"
                  onClick={closeMenu}
                >
                  Go to Dashboard &rarr;
                </Link>
              ) : (
                <>
                  <Link
                    to="/signup"
                    className="block w-full text-center py-3 text-base font-semibold bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
                    onClick={closeMenu}
                  >
                    Sign up
                  </Link>
                  <Link
                    to="/login"
                    className="block w-full text-center py-3 text-base font-semibold text-gray-700 border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
                    onClick={closeMenu}
                  >
                    Log in
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
